import planVersionService from "@plannel/services/data/plan-version-service";
import debug from "@plannel/utils/debug-util";
import { isArray, isEmpty, isEqual, size } from "lodash";
import { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import GavelIcon from "@mui/icons-material/Gavel";
import { green, grey } from "@mui/material/colors";
import dateUtils from "@plannel/utils/date-util";
import codeUtils from "@plannel/pages/utils/code-util";
import { LockRounded } from "@mui/icons-material";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";

/**
 *
 * @param {versionDto} selectedVersion Current selected version ex) { id:12345.., name: "version", ...}
 * @param {versionDto} reduxVersion redux version of the current page ex) { id:12345.., name: "version", ...}
 * @param {boolean} reload reload this component
 * @param {boolean} setReload Reload the page that contains this component
 * @param {String, Array} status version code ex) [SCENARIO], [RELEASED]...
 * @param {boolean} isMain select only main scenario of serveral scenario 
 */

const DpVersionSelect = props => {
  const { t, selectedVersion, reduxVersion, reload, setReload, status, isMain = false } = props;

  const [versionList, setVersionList] = useState([]);
  const [curVersion, setCurVersion] = useState(null);
  const { limitVersion, dpVersion } = codeUtils
  const { RELEASED, SCENARIO, REFERENCE } = dpVersion;

  const sortReleseList = list => {
    list.sort((a, b) => {
      if (a.statusCd !== b.statusCd) {
        return b.statusCd.localeCompare(a.statusCd);
      } else {
        const compareTs = isEqual(a.statusCd, RELEASED) ? "releaseTs" : "createdTs";
        if (a[compareTs] > b[compareTs]) {
          return -1;
        } else if (a[compareTs] < b[compareTs]) {
          return 1;
        }
        return 0;
      }
    });

    if (size(list) > limitVersion) {
      const limitList = list.slice(0, limitVersion)
      return limitList;
    }
    return list;
  };

  const setSelectedVersion = version => {
    if (!isEqual(selectedVersion?.id, version.id)) {
      setCurVersion(version);
      selectedVersion(version);
    }
  }

  const handleChangeSelect = value => {
    setSelectedVersion(value);
  };

  const filterVersinList = (results) => {
    const filterList = [];
    for (const row of results) {
      if (row.statusCd === SCENARIO && row.parent === null) {
        filterList.push(row);
      } else if (row.statusCd === RELEASED || row.statusCd === REFERENCE){
        filterList.push(row);
      }
    }
    return filterList;
  }

  const retrieveAllPlanVersion = () => {
    let statusCds = status ?? [RELEASED, SCENARIO, REFERENCE];
    statusCds = isArray(statusCds) ? statusCds : [statusCds];
    planVersionService.getAll({ moduleCd: codeUtils.module.DP, statusCds, orderByColumn: "createdTs", sortType: "desc" })
      .then(response => {
        let results = response.data.results;
        if (results) {
          if (isMain && !isEmpty(results)) {
            results = filterVersinList(results);
          }
          const sortList = sortReleseList(results);
          setVersionList(sortList);
          if (size(reduxVersion) > 0) {
            const filterVersion = sortList.filter(version => isEqual(version.id, reduxVersion.id));
            if (isEmpty(filterVersion)) {
              setSelectedVersion(sortList[0]);
            } else {
              setSelectedVersion(filterVersion[0]);
            }
          } else {
            setSelectedVersion(sortList[0]);
          }
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        if (setReload) {
          setReload(false);
        }
      });
  };

  useEffect(() => {
    retrieveAllPlanVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reload) {
      retrieveAllPlanVersion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const getIconColor = version => {
    if (isEqual(version.statusCd, SCENARIO)) {
      return green[600];
    } else if (isEqual(version.statusCd, REFERENCE)) {
      return "#000";
    }
    return grey[900];
  };

  const getIcon = version => {
    if (!version) {
      return null;
    }
    if (isEqual(version.statusCd, SCENARIO)) {
      return ViewInArIcon;
    } else if (isEqual(version.statusCd, REFERENCE)) {
      return LockRounded;
    }
    return GavelIcon;
  };

  const setToolTip = version => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE: startDate, BUCKET_UNIT: unit, PLANNING_RANGE: range, START_DAY: startDay } = version.planConfigObj;
      let format = "yyyy-MM";
      if (codeUtils.isWeekBucket(unit)) {
        format = "yyyy-MM-dd";
      }
      return `${dateUtils.formatDate(startDate, format)} ~
      ${dateUtils.formatDate(dateUtils.calculateRangeDate(startDate, unit, startDay, range, true), format)}`;
    }
    return null;
  };

  return (
    <VersionAutocomplete
      options={versionList}
      value={curVersion}
      onChange={(_, newValue) => handleChangeSelect(newValue)}
      label={t("version")}
      getIcon={getIcon}
      getIconColor={getIconColor}
      getTooltip={setToolTip}
      tooltipPlacement="top-start"
      minWidth={250}
      inputIconSx={{ ml: 1 }}
    />
  );
};

export default withTranslation()(DpVersionSelect);